import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface MatchupWithPartner {
  id: string;
  round_id: string;
  user_id_1: string;
  user_id_2: string;
  ai_reason: string;
  created_at: string;
  partner_profile: {
    user_id: string;
    full_name: string;
    avatar_url: string | null;
    headline: string | null;
    company: string | null;
    role: string | null;
    industry: string | null;
    city_cohort: string;
    bio: string | null;
    linkedin_url: string | null;
    pronouns: string | null;
    is_mentor: boolean;
  } | null;
  round_week_of: string;
}

export function useMatchups() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['matchups', user?.id],
    queryFn: async (): Promise<MatchupWithPartner[]> => {
      if (!user?.id) return [];

      // Fetch matchups where user is either side
      const { data: asUser1, error: err1 } = await supabase
        .from('matchups')
        .select('*, matchup_rounds!inner(week_of)')
        .eq('user_id_1', user.id)
        .order('created_at', { ascending: false });

      const { data: asUser2, error: err2 } = await supabase
        .from('matchups')
        .select('*, matchup_rounds!inner(week_of)')
        .eq('user_id_2', user.id)
        .order('created_at', { ascending: false });

      if (err1) throw err1;
      if (err2) throw err2;

      const allMatchups = [...(asUser1 ?? []), ...(asUser2 ?? [])];
      if (allMatchups.length === 0) return [];

      // Get partner profiles
      const partnerIds = allMatchups.map((m) =>
        m.user_id_1 === user.id ? m.user_id_2 : m.user_id_1
      );
      const uniquePartnerIds = [...new Set(partnerIds)];

      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('user_id, full_name, avatar_url, headline, company, role, industry, city_cohort, bio, linkedin_url, pronouns, is_mentor')
        .in('user_id', uniquePartnerIds);

      if (profilesError) throw profilesError;

      const profileMap = new Map(profiles?.map((p) => [p.user_id, p]));

      return allMatchups
        .map((m) => {
          const partnerId = m.user_id_1 === user.id ? m.user_id_2 : m.user_id_1;
          return {
            id: m.id,
            round_id: m.round_id,
            user_id_1: m.user_id_1,
            user_id_2: m.user_id_2,
            ai_reason: m.ai_reason,
            created_at: m.created_at,
            partner_profile: profileMap.get(partnerId) ?? null,
            round_week_of: (m as any).matchup_rounds?.week_of ?? '',
          };
        })
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    },
    enabled: !!user?.id,
  });
}

export function useCurrentMatchups() {
  const { data: matchups, ...rest } = useMatchups();

  if (!matchups || matchups.length === 0) {
    return { currentMatchups: [], pastMatchups: [], ...rest };
  }

  // Group by round_week_of
  const latestWeek = matchups[0].round_week_of;
  const currentMatchups = matchups.filter((m) => m.round_week_of === latestWeek);
  const pastMatchups = matchups.filter((m) => m.round_week_of !== latestWeek);

  return { currentMatchups, pastMatchups, ...rest };
}
